import React, { useState, useEffect, useCallback } from 'react';
import { toChileInput, fromChileInput } from '../../utils/chileTime';
import { useParams, useNavigate } from 'react-router-dom';
import { trainingsApi, enrollmentsApi, companiesApi, lifeSavingApi, relatorApi, courseRequestsApi, courseTestsApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../../components/ui/tabs';
import {
  ArrowLeft, FloppyDisk, Plus, Trash, MagnifyingGlass,
  Users, CalendarBlank, Play, XCircle, CheckCircle, Exam, Buildings, Warning, ClockCounterClockwise, HeartBreak, PencilSimple, PaperPlaneTilt, QrCode, Envelope
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import CourseCountBadge from '../../components/CourseCountBadge';

const MODALITIES = [
  'E-Learning', 'E-Learning Teórico-Práctico', 'Presencial',
  'Presencial Teórico-Práctico', 'Teórico-Práctico', 'Video'
];

export default function TrainingDetailPage() {
  const { trainingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [training, setTraining] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('info');

  // Editable fields
  const [editData, setEditData] = useState({
    scheduled_date: '', modality: '', connection_url: '', max_participants: 30, reserved_spots: 0
  });

  // Enrollments
  const [enrollments, setEnrollments] = useState([]);
  const pendingEnrollments = enrollments.filter(e => e.status === 'pending');
  const confirmedEnrollments = enrollments.filter(e => e.status !== 'pending' && e.status !== 'rejected');

  // Course requests (solicitudes)
  const [courseRequests, setCourseRequests] = useState([]);
  const pendingRequests = courseRequests.filter(r => r.status === 'pending');
  const [approvingRequest, setApprovingRequest] = useState(null);

  // Worker assignment
  const [showWorkerSearch, setShowWorkerSearch] = useState(false);
  const [workerSearch, setWorkerSearch] = useState('');
  const [workerCompanyFilter, setWorkerCompanyFilter] = useState('');
  const [availableWorkers, setAvailableWorkers] = useState([]);
  const [loadingWorkers, setLoadingWorkers] = useState(false);
  const [enrollingWorker, setEnrollingWorker] = useState(false);
  const [removingEnrollment, setRemovingEnrollment] = useState(null);

  // Companies for filter
  const [allCompanies, setAllCompanies] = useState([]);

  // Direct evaluation
  const [evaluatingEnrollment, setEvaluatingEnrollment] = useState(null);
  const [evalData, setEvalData] = useState({ result: 'approved', score: 100, notes: '' });

  // Life-saving violation
  const [violationWorker, setViolationWorker] = useState(null);
  const [violationReason, setViolationReason] = useState('');

  // Conformar modal
  const [showConformarModal, setShowConformarModal] = useState(false);
  const [sendingInvites, setSendingInvites] = useState(false);
  const [showTestUrl, setShowTestUrl] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewWorkerId, setPreviewWorkerId] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Envío de correos con CC
  const [showSendModal, setShowSendModal] = useState(false);
  const [singleSendEnrollment, setSingleSendEnrollment] = useState(null);
  const [ccEmail, setCcEmail] = useState('');
  const [conformarCc, setConformarCc] = useState('');

  // Relator assignment
  const [relators, setRelators] = useState([]);
  const [selectedRelator, setSelectedRelator] = useState('');

  // Test bank (banco de preguntas) assignment
  const [courseTests, setCourseTests] = useState([]);
  const [selectedTestBank, setSelectedTestBank] = useState('');

  const fetchTraining = useCallback(async () => {
    try {
      const res = await trainingsApi.getById(trainingId);
      setTraining(res.data);
      const d = res.data;
      setEditData({
        scheduled_date: d.scheduled_date ? toChileInput(d.scheduled_date) : '',
        modality: d.modality || '',
        connection_url: d.connection_url || '',
        max_participants: d.max_participants || 30,
        reserved_spots: d.reserved_spots || 0
      });
      setSelectedRelator(d.relator_id || '');
      // Load banks assigned to the course and set default selection
      if (d.course_id) {
        try {
          const tRes = await courseTestsApi.getAssignedTests(d.course_id);
          const banks = tRes.data || [];
          setCourseTests(banks);
          const bankIds = banks.map(b => b.id);
          if (d.default_test_bank_id && bankIds.includes(d.default_test_bank_id)) {
            setSelectedTestBank(d.default_test_bank_id);
          } else if (banks.length >= 1) {
            setSelectedTestBank(banks[0].id);
          } else {
            setSelectedTestBank('');
          }
        } catch { setCourseTests([]); }
      } else {
        setCourseTests([]);
        setSelectedTestBank('');
      }
    } catch {
      toast.error('Capacitación no encontrada');
      navigate('/admin/trainings');
    } finally {
      setLoading(false);
    }
  }, [trainingId, navigate]);

  const fetchEnrollments = useCallback(async () => {
    try {
      const res = await enrollmentsApi.getAll({ training_id: trainingId });
      setEnrollments(res.data);
    } catch { /* ignore */ }
  }, [trainingId]);

  const fetchRequests = useCallback(async () => {
    try {
      const res = await courseRequestsApi.getAll({ training_id: trainingId, status: 'pending' });
      setCourseRequests(res.data);
    } catch { /* ignore */ }
  }, [trainingId]);

  useEffect(() => {
    fetchTraining();
    fetchEnrollments();
    fetchRequests();
    companiesApi.getAll({ page_size: 999 }).then(res => setAllCompanies(res.data.items || res.data)).catch(() => {});
    relatorApi.getRelators().then(res => setRelators(res.data)).catch(() => {});
  }, [fetchTraining, fetchEnrollments, fetchRequests]);

  // Search available workers
  const searchWorkers = useCallback(async (term, companyId) => {
    setLoadingWorkers(true);
    try {
      const res = await trainingsApi.getAvailableWorkers(trainingId, term || undefined, companyId || undefined);
      setAvailableWorkers(res.data);
    } catch { toast.error('Error al buscar'); }
    finally { setLoadingWorkers(false); }
  }, [trainingId]);

  useEffect(() => {
    if (showWorkerSearch) searchWorkers(workerSearch, workerCompanyFilter === 'all' ? '' : workerCompanyFilter);
  }, [showWorkerSearch, workerSearch, workerCompanyFilter, searchWorkers]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...editData,
        scheduled_date: fromChileInput(editData.scheduled_date),
        max_participants: parseInt(editData.max_participants),
        reserved_spots: parseInt(editData.reserved_spots) || 0,
        relator_id: selectedRelator && selectedRelator !== 'none' ? selectedRelator : null,
        default_test_bank_id: selectedTestBank || null
      };
      await trainingsApi.update(trainingId, payload);
      toast.success('Capacitacion actualizada');
      fetchTraining();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const handleEnrollWorker = async (workerId) => {
    setEnrollingWorker(true);
    try {
      await trainingsApi.enrollWorker(trainingId, workerId);
      toast.success('Trabajador inscrito');
      fetchEnrollments();
      searchWorkers(workerSearch, workerCompanyFilter);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al inscribir');
    } finally { setEnrollingWorker(false); }
  };

  const handleLifeSavingViolation = async () => {
    if (!violationWorker || !training?.course_id) return;
    try {
      await lifeSavingApi.create({
        worker_id: violationWorker.id,
        course_id: training.course_id,
        reason: violationReason.trim() || 'Transgresion de Regla que salva vidas',
      });
      toast.success('Transgresion registrada. El trabajador puede ser reinscrito.');
      setViolationWorker(null);
      setViolationReason('');
      searchWorkers(workerSearch, workerCompanyFilter);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al registrar transgresion');
    }
  };

  const handleRemoveEnrollment = async (enrollmentId) => {
    try {
      await enrollmentsApi.delete(enrollmentId);
      toast.success('Inscripción eliminada');
      setRemovingEnrollment(null);
      fetchEnrollments();
    } catch { toast.error('Error al eliminar'); }
  };

  const handleDirectEvaluate = async () => {
    try {
      await enrollmentsApi.evaluate(evaluatingEnrollment.id, evalData);
      toast.success(evalData.result === 'approved' ? 'Trabajador aprobado' : 'Trabajador reprobado');
      setEvaluatingEnrollment(null);
      setEvalData({ result: 'approved', score: 100, notes: '' });
      fetchEnrollments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al evaluar');
    }
  };

  const handleApproveEnrollment = async (enrollmentId) => {
    try {
      await enrollmentsApi.approve(enrollmentId);
      toast.success('Inscripción aprobada');
      fetchEnrollments();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al aprobar'); }
  };

  const handleRejectEnrollment = async (enrollmentId) => {
    try {
      await enrollmentsApi.reject(enrollmentId);
      toast.success('Inscripción rechazada');
      fetchEnrollments();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al rechazar'); }
  };

  const handleApproveRequest = async (requestId) => {
    setApprovingRequest(requestId);
    try {
      const res = await courseRequestsApi.approveToTraining(requestId, trainingId);
      toast.success(res.data.message);
      fetchRequests();
      fetchEnrollments();
      fetchTraining();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al aprobar'); }
    finally { setApprovingRequest(null); }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      await courseRequestsApi.reject(requestId);
      toast.success('Solicitud rechazada');
      fetchRequests();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al rechazar'); }
  };


  const [autoAssigning, setAutoAssigning] = useState(false);
  const handleAutoAssign = async () => {
    setAutoAssigning(true);
    try {
      const res = await trainingsApi.autoAssign(trainingId);
      const { assigned, total_pending_remaining, message } = res.data;
      if (assigned > 0) {
        toast.success(message);
      } else {
        toast.info(message);
      }
      fetchEnrollments();
      fetchTraining();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al auto-asignar');
    } finally { setAutoAssigning(false); }
  };

  const handleConformar = async (sendEmails) => {
    try {
      const res = await trainingsApi.conformar(trainingId, sendEmails, sendEmails ? (conformarCc.trim() || undefined) : undefined);
      const msg = sendEmails ? `Capacitación conformada. ${res.data.notifications_sent} invitación(es) enviada(s)${res.data.notifications_failed ? `, ${res.data.notifications_failed} fallida(s)` : ''}` : 'Capacitación conformada (sin envío de correos)';
      toast.success(msg);
      setShowConformarModal(false);
      setConformarCc('');
      fetchTraining();
      fetchEnrollments();
    } catch { toast.error('Error al conformar'); }
  };

  const handleSendInvitations = async () => {
    setSendingInvites(true);
    try {
      const res = await trainingsApi.sendInvitations(trainingId, ccEmail.trim() || undefined);
      toast.success(`${res.data.notifications_sent} invitación(es) enviada(s)${res.data.notifications_failed ? `, ${res.data.notifications_failed} fallida(s)` : ''}`);
      setShowSendModal(false);
      setCcEmail('');
      fetchEnrollments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al enviar invitaciones');
    } finally {
      setSendingInvites(false);
    }
  };

  const handleSendSingle = async (workerId, cc) => {
    try {
      const res = await trainingsApi.sendInvitation(trainingId, workerId, cc || undefined);
      toast.success(res.data.message || 'Invitación enviada');
      setSingleSendEnrollment(null);
      setCcEmail('');
      fetchEnrollments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al enviar invitación');
    }
  };

  const handleSetActivation = async (mode, active) => {
    try {
      await trainingsApi.setTestActivation(trainingId, { mode, active });
      fetchTraining();
      toast.success('Estado de la prueba actualizado');
    } catch { toast.error('Error al actualizar la prueba'); }
  };

  const openPreview = async (workerId) => {
    setShowPreview(true);
    setLoadingPreview(true);
    try {
      const res = await trainingsApi.invitationPreview(trainingId, workerId || undefined);
      setPreviewData(res.data);
      if (!workerId && res.data.recipient_email) setPreviewWorkerId('');
    } catch (e) {
      toast.error('No se pudo generar la vista previa');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleDesconformar = async () => {
    try {
      await trainingsApi.desconformar(trainingId);
      toast.success('Capacitación vuelve a Sin Conformar');
      fetchTraining();
    } catch { toast.error('Error'); }
  };

  const handleRealizada = async () => {
    try {
      await trainingsApi.realizada(trainingId);
      toast.success('Capacitación marcada como Realizada');
      fetchTraining();
    } catch { toast.error('Error'); }
  };

  const getStatusBadge = (status) => {
    const config = {
      sin_conformar: { label: 'Sin Conformar', cls: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20' },
      conformada: { label: 'Conformada', cls: 'bg-blue-500/10 text-blue-500 border-blue-500/20' },
      realizada: { label: 'Realizada', cls: 'bg-[#2BBAA4]/10 text-[#2BBAA4] border-[#2BBAA4]/20' },
    };
    const c = config[status] || config.sin_conformar;
    return <Badge className={`${c.cls} border text-sm`}>{c.label}</Badge>;
  };

  if (loading || !training) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2BBAA4]" /></div>;
  }

  const isEditable = training.status === 'sin_conformar';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="training-detail-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/trainings')} className="text-[#2B4B8C]/60 hover:text-[#2B4B8C] -ml-2" data-testid="back-to-trainings">
          <ArrowLeft size={20} className="mr-1" /> Volver
        </Button>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-[#2B4B8C]">{training.name}</h1>
            {getStatusBadge(training.status)}
          </div>
          <p className="text-[#2B4B8C]/50 text-sm mt-1">
            {training.training_type} &middot; Código: <span className="font-mono">{training.code}</span>
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => { setPreviewWorkerId(''); openPreview(''); }} variant="outline" className="border-[#2B4B8C]/20 text-[#2B4B8C] hover:bg-[#f8fafc]" data-testid="preview-email-btn">
            <Envelope size={16} className="mr-1.5" /> Vista previa correo
          </Button>
          <Button onClick={() => setShowTestUrl(true)} variant="outline" className="border-[#2B4B8C]/20 text-[#2B4B8C] hover:bg-[#f8fafc]" data-testid="view-test-url-btn">
            <QrCode size={16} className="mr-1.5" /> Ver QR y URL de la prueba
          </Button>
          {training.status === 'sin_conformar' && (
            <Button onClick={() => setShowConformarModal(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="conformar-btn">
              <CheckCircle size={16} className="mr-1.5" /> Conformar
            </Button>
          )}
          {training.status === 'conformada' && (
            <>
              <Button onClick={() => { setCcEmail(''); setShowSendModal(true); }} disabled={sendingInvites} variant="outline" className="border-[#2BBAA4]/40 text-[#2BBAA4] hover:bg-[#2BBAA4]/5" data-testid="send-invitations-btn">
                <PaperPlaneTilt size={16} className="mr-1.5" /> {sendingInvites ? 'Enviando...' : 'Enviar invitaciones'}
              </Button>
              <Button onClick={handleDesconformar} variant="outline" className="border-yellow-500/30 text-yellow-600 hover:bg-yellow-50" data-testid="desconformar-btn">
                <ArrowLeft size={16} className="mr-1.5" /> Desconformar
              </Button>
              <Button onClick={handleRealizada} className="bg-[#2BBAA4] hover:bg-[#239986] text-black" data-testid="realizada-btn">
                <CheckCircle size={16} className="mr-1.5" /> Marcar Realizada
              </Button>
            </>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#f8fafc] border border-[#2B4B8C]/10">
          <TabsTrigger value="info" className="data-[state=active]:bg-white data-[state=active]:text-[#2B4B8C]" data-testid="tab-info">
            <CalendarBlank size={16} className="mr-1.5" /> Información
          </TabsTrigger>
          <TabsTrigger value="pending" className="data-[state=active]:bg-white data-[state=active]:text-[#2B4B8C]" data-testid="tab-pending">
            <ClockCounterClockwise size={16} className="mr-1.5" /> Pendientes
            {(pendingEnrollments.length + pendingRequests.length) > 0 && (
              <Badge className="ml-1.5 bg-amber-500/10 text-amber-600 border-amber-500/20 border text-[10px]">{pendingEnrollments.length + pendingRequests.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="workers" className="data-[state=active]:bg-white data-[state=active]:text-[#2B4B8C]" data-testid="tab-workers">
            <Users size={16} className="mr-1.5" /> Inscritos ({confirmedEnrollments.length})
          </TabsTrigger>
        </TabsList>

        {/* Info Tab */}
        <TabsContent value="info" className="space-y-4 mt-4">
          {!isEditable && (
            <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-700">
              Esta capacitación está <strong>{training.status === 'conformada' ? 'Conformada' : 'Realizada'}</strong>. {training.status === 'conformada' ? 'Para editar, primero debe Desconformar.' : 'No se permiten cambios.'}
            </div>
          )}
          <Card className="bg-white border-[#2B4B8C]/10">
            <CardContent className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-5">
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#2B4B8C]/70">Fecha y Hora</Label>
                  <Input
                    type="datetime-local"
                    value={editData.scheduled_date}
                    onChange={(e) => setEditData({ ...editData, scheduled_date: e.target.value })}
                    className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
                    disabled={!isEditable}
                    data-testid="edit-date"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#2B4B8C]/70">Modalidad</Label>
                  <Select value={editData.modality} onValueChange={(v) => setEditData({ ...editData, modality: v })} disabled={!isEditable}>
                    <SelectTrigger className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="edit-modality">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#2B4B8C]/10">
                      {MODALITIES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-5">
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#2B4B8C]/70">URL de Conexión (Meet, Teams, Zoom...)</Label>
                  <Input
                    value={editData.connection_url}
                    onChange={(e) => setEditData({ ...editData, connection_url: e.target.value })}
                    className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
                    placeholder="https://meet.google.com/..."
                    disabled={!isEditable}
                    data-testid="edit-url"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#2B4B8C]/70">Max. Participantes</Label>
                  <Input
                    type="number"
                    value={editData.max_participants}
                    onChange={(e) => setEditData({ ...editData, max_participants: parseInt(e.target.value) || 30 })}
                    className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
                    disabled={!isEditable}
                    data-testid="edit-max"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#2B4B8C]/70">Cupos Reservados (admin)</Label>
                  <Input
                    type="number"
                    min="0"
                    max={editData.max_participants - 1}
                    value={editData.reserved_spots}
                    onChange={(e) => setEditData({ ...editData, reserved_spots: Math.min(parseInt(e.target.value) || 0, editData.max_participants - 1) })}
                    className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
                    disabled={!isEditable}
                    data-testid="edit-reserved-spots"
                  />
                </div>
              </div>
              {(training.reserved_spots > 0 || editData.reserved_spots > 0) && (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200/50 text-sm" data-testid="cupos-info">
                  <div className="flex items-center gap-4 text-[#2B4B8C]/70">
                    <span><strong className="text-[#2B4B8C]">{editData.reserved_spots}</strong> cupos reservados (admin)</span>
                    <span className="text-[#2B4B8C]/30">|</span>
                    <span><strong className="text-[#2B4B8C]">{editData.max_participants - editData.reserved_spots}</strong> cupos abiertos (auto-asignar)</span>
                    <span className="text-[#2B4B8C]/30">|</span>
                    <span><strong className="text-[#2B4B8C]">{editData.max_participants}</strong> total</span>
                  </div>
                </div>
              )}
              {/* Relator assignment */}
              <div className="space-y-1.5">
                <Label className="text-xs text-[#2B4B8C]/70">Relator Asignado</Label>
                <Select value={selectedRelator} onValueChange={setSelectedRelator} disabled={!isEditable}>
                  <SelectTrigger className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="relator-select">
                    <SelectValue placeholder="Sin relator asignado" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#2B4B8C]/10">
                    <SelectItem value="none">Sin relator</SelectItem>
                    {relators.map(r => (
                      <SelectItem key={r.id} value={r.id}>{r.name} ({r.email})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {training.relator_name && (
                  <p className="text-xs text-[#2BBAA4]">Actual: {training.relator_name}</p>
                )}
              </div>
              {/* Banco de Preguntas assignment */}
              <div className="space-y-1.5" data-testid="test-bank-assignment">
                <Label className="text-xs text-[#2B4B8C]/70 flex items-center gap-1.5">
                  <Exam size={14} className="text-[#2BBAA4]" /> Banco de Preguntas de la Evaluación
                </Label>
                {courseTests.length === 0 ? (
                  <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    El curso no tiene bancos de preguntas asignados. Asigna uno desde la configuración del curso.
                  </p>
                ) : courseTests.length === 1 ? (
                  <div className="text-sm text-[#2B4B8C] bg-[#f8fafc] border border-[#2B4B8C]/10 rounded-lg px-3 py-2">
                    <span className="font-medium">{courseTests[0].name}</span>
                    <span className="text-[#2B4B8C]/50"> · {courseTests[0].questions_count} preguntas</span>
                    <span className="ml-2 text-xs text-[#2BBAA4]">(por defecto)</span>
                  </div>
                ) : (
                  <Select value={selectedTestBank} onValueChange={setSelectedTestBank} disabled={!isEditable}>
                    <SelectTrigger className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="test-bank-select">
                      <SelectValue placeholder="Seleccionar banco de preguntas" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#2B4B8C]/10 max-h-64">
                      {courseTests.map(b => (
                        <SelectItem key={b.id} value={b.id}>{b.name} ({b.questions_count} preguntas)</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {courseTests.length > 1 && (
                  <p className="text-xs text-[#2B4B8C]/50">El curso tiene varios bancos. Elige cuál se usará en la evaluación de esta capacitación.</p>
                )}
              </div>
              {/* Read-only info */}
              {(() => {
                const bank = courseTests.find(b => b.id === selectedTestBank) || courseTests[0];
                const perTest = bank ? (bank.questions_per_test || bank.questions_count) : training.questions_count;
                const items = [
                  ['Tipo', training.training_type, 'info-type'],
                  ['Duración capacitación', `${training.duration_minutes} min`, 'info-duration'],
                  ['% Aprobación', `${bank ? bank.passing_percentage : training.passing_percentage}%`, 'info-passing'],
                  ['Intentos', bank ? bank.max_attempts : training.max_evaluation_attempts, 'info-attempts'],
                  ['Preguntas por prueba', perTest, 'info-questions'],
                  ['Tiempo de prueba', `${bank ? bank.time_limit_minutes : training.evaluation_time_minutes} min`, 'info-test-time'],
                ];
                return (
                  <div className="space-y-1.5 pt-2 border-t border-[#2B4B8C]/5">
                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
                      {items.map(([label, value, tid]) => (
                        <div key={tid} className="p-3 rounded-lg bg-[#f8fafc]">
                          <p className="text-xs text-[#2B4B8C]/50">{label}</p>
                          <p className="text-sm text-[#2B4B8C] font-medium" data-testid={tid}>{value ?? '-'}</p>
                        </div>
                      ))}
                    </div>
                    {bank && <p className="text-xs text-[#2B4B8C]/50">% aprobación, intentos, preguntas y tiempo de prueba se toman del Banco de Preguntas.</p>}
                  </div>
                );
              })()}
              {isEditable && (
                <div className="flex justify-end pt-2">
                  <Button onClick={handleSave} disabled={saving} className="bg-[#2BBAA4] text-black hover:bg-[#239986]" data-testid="save-training-btn">
                    <FloppyDisk size={16} className="mr-1.5" /> {saving ? 'Guardando...' : 'Guardar Cambios'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pending Enrollments Tab */}
        <TabsContent value="pending" className="space-y-4 mt-4">
          {/* Course Requests (Solicitudes al curso) */}
          {pendingRequests.length > 0 && (
            <div className="space-y-3" data-testid="course-requests-section">
              <h3 className="text-sm font-semibold text-[#2B4B8C] flex items-center gap-2">
                <ClockCounterClockwise size={16} /> Solicitudes al curso ({pendingRequests.reduce((s, r) => s + r.workers_count, 0)} trabajadores)
              </h3>
              {pendingRequests.map((req) => (
                <Card key={req.id} className="bg-amber-50/50 border-amber-200/50" data-testid={`request-${req.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <span className="text-sm font-medium text-[#2B4B8C]">{req.company_name}</span>
                        <span className="text-xs text-[#2B4B8C]/40 ml-3">{req.workers_count} trabajador(es)</span>
                        {req.observation && <span className="text-xs text-[#2B4B8C]/40 ml-2">- {req.observation}</span>}
                        <span className="text-xs text-[#2B4B8C]/30 ml-3">{req.created_at ? new Date(req.created_at).toLocaleDateString('es-CL') : ''}</span>
                        {req.preferred_date && (
                          <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-md bg-amber-100 border border-amber-300 px-2 py-0.5" data-testid={`preferred-date-${req.id}`}>
                            <CalendarBlank size={13} className="text-amber-700" />
                            <span className="text-xs text-amber-800 font-medium">Día preferido: {req.preferred_date}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" onClick={() => handleApproveRequest(req.id)} disabled={approvingRequest === req.id} className="bg-green-500 text-white hover:bg-green-600 h-7 text-xs px-3" data-testid={`approve-request-${req.id}`}>
                          <CheckCircle size={14} className="mr-1" /> {approvingRequest === req.id ? 'Inscribiendo...' : 'Aprobar e Inscribir'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleRejectRequest(req.id)} className="border-red-300 text-red-500 hover:bg-red-50 h-7 text-xs px-3" data-testid={`reject-request-${req.id}`}>
                          <XCircle size={14} className="mr-1" /> Rechazar
                        </Button>
                      </div>
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-[#2B4B8C]/10 hover:bg-transparent">
                          <TableHead className="text-[#2B4B8C]/60 text-xs py-1">RUT</TableHead>
                          <TableHead className="text-[#2B4B8C]/60 text-xs py-1">Nombre</TableHead>
                          <TableHead className="text-[#2B4B8C]/60 text-xs py-1">Cargo</TableHead>
                          <TableHead className="text-[#2B4B8C]/60 text-xs py-1">Empresa</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {req.workers.map((w) => (
                          <TableRow key={w.detail_id} className="border-[#2B4B8C]/5">
                            <TableCell className="text-[#2B4B8C]/60 text-xs py-1.5">{w.rut || '-'}</TableCell>
                            <TableCell className="text-[#2B4B8C] text-xs py-1.5 font-medium">{w.name}</TableCell>
                            <TableCell className="text-[#2B4B8C]/60 text-xs py-1.5">{w.cargo || '-'}</TableCell>
                            <TableCell className="text-[#2B4B8C]/60 text-xs py-1.5">{w.company_name || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Auto-assign bar */}
          {pendingEnrollments.length > 0 && (training.reserved_spots || 0) > 0 && (
            <div className="flex items-center justify-between p-3 rounded-lg bg-blue-50 border border-blue-200" data-testid="auto-assign-bar">
              <div className="text-sm text-blue-700">
                <strong>{pendingEnrollments.length}</strong> pendiente(s) &middot; Cupos abiertos: <strong>{Math.max(0, training.max_participants - (training.reserved_spots || 0))}</strong> de {training.max_participants}
              </div>
              <Button size="sm" onClick={handleAutoAssign} disabled={autoAssigning} className="bg-blue-600 hover:bg-blue-700 text-white h-8 text-xs" data-testid="auto-assign-btn">
                {autoAssigning ? 'Asignando...' : 'Auto-asignar Aleatoriamente'}
              </Button>
            </div>
          )}

          {/* Pending enrollments table */}
          {pendingEnrollments.length > 0 && (
            <Card className="bg-white border-[#2B4B8C]/10">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#2B4B8C]/10 hover:bg-transparent">
                      <TableHead className="text-[#2B4B8C]/60">RUT</TableHead>
                      <TableHead className="text-[#2B4B8C]/60">Nombre</TableHead>
                      <TableHead className="text-[#2B4B8C]/60">Cargo</TableHead>
                      <TableHead className="text-[#2B4B8C]/60">Empresa</TableHead>
                      <TableHead className="text-[#2B4B8C]/60">Nº Vez</TableHead>
                      <TableHead className="text-[#2B4B8C]/60">Fecha Solicitud</TableHead>
                      <TableHead className="text-[#2B4B8C]/60 text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingEnrollments.map((e) => (
                      <TableRow key={e.id} className="border-[#2B4B8C]/5 hover:bg-amber-50/30">
                        <TableCell className="text-[#2B4B8C]/60">{e.worker_rut || '-'}</TableCell>
                        <TableCell className="text-[#2B4B8C] font-medium">{e.worker_name}</TableCell>
                        <TableCell className="text-[#2B4B8C]/60">{e.worker_cargo || '-'}</TableCell>
                        <TableCell className="text-[#2B4B8C]/60">{e.company_name || '-'}</TableCell>
                        <TableCell><CourseCountBadge count={e.course_count} /></TableCell>
                        <TableCell className="text-[#2B4B8C]/60 text-sm">
                          {e.enrolled_at ? new Date(e.enrolled_at).toLocaleDateString('es-CL') : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center gap-2 justify-end">
                            <Button size="sm" onClick={() => handleApproveEnrollment(e.id)} className="bg-green-500 text-white hover:bg-green-600 h-7 text-xs px-3" data-testid={`approve-${e.id}`}>
                              <CheckCircle size={14} className="mr-1" /> Aceptar
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleRejectEnrollment(e.id)} className="border-red-300 text-red-500 hover:bg-red-50 h-7 text-xs px-3" data-testid={`reject-${e.id}`}>
                              <XCircle size={14} className="mr-1" /> Rechazar
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {pendingEnrollments.length === 0 && pendingRequests.length === 0 && (
            <div className="text-center py-8 text-[#2B4B8C]/50 text-sm">No hay solicitudes ni inscripciones pendientes</div>
          )}
        </TabsContent>

        {/* Workers Tab */}
        <TabsContent value="workers" className="space-y-4 mt-4">
          {/* Cupos summary */}
          {(training.reserved_spots || 0) > 0 && (
            <div className="grid grid-cols-3 gap-3" data-testid="cupos-summary">
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200/50 text-center">
                <p className="text-xs text-amber-600/70">Reservados (admin)</p>
                <p className="text-lg font-bold text-amber-700">{training.reserved_spots}</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200/50 text-center">
                <p className="text-xs text-blue-600/70">Abiertos (auto-asignar)</p>
                <p className="text-lg font-bold text-blue-700">{training.max_participants - (training.reserved_spots || 0)}</p>
              </div>
              <div className="p-3 rounded-lg bg-[#2BBAA4]/5 border border-[#2BBAA4]/20 text-center">
                <p className="text-xs text-[#2BBAA4]/70">Confirmados</p>
                <p className="text-lg font-bold text-[#2BBAA4]">{confirmedEnrollments.length} / {training.max_participants}</p>
              </div>
            </div>
          )}
          {!isEditable && (
            <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-700">
              {training.status === 'conformada' ? 'Para agregar trabajadores, primero debe Desconformar.' : 'Capacitación Realizada. No se permiten cambios.'}
            </div>
          )}
          {isEditable && (
            <div className="flex justify-end">
              <Button onClick={() => { setShowWorkerSearch(!showWorkerSearch); setWorkerSearch(''); setWorkerCompanyFilter(''); }} className="bg-[#2BBAA4] text-black hover:bg-[#239986]" data-testid="add-worker-btn">
                <Plus size={18} className="mr-2" /> Asignar Trabajador
              </Button>
            </div>
          )}

          {showWorkerSearch && (
            <Card className="bg-[#f8fafc] border-[#2B4B8C]/10">
              <CardContent className="p-5 space-y-3">
                <p className="font-medium text-[#2B4B8C]">Buscar Trabajador</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-[#2B4B8C]/70"><Buildings size={14} className="inline mr-1" />Filtrar por Empresa</Label>
                    <Select value={workerCompanyFilter} onValueChange={setWorkerCompanyFilter}>
                      <SelectTrigger className="bg-white border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="filter-company">
                        <SelectValue placeholder="Todas las empresas" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-[#2B4B8C]/10">
                        <SelectItem value="all">Todas las empresas</SelectItem>
                        {allCompanies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-[#2B4B8C]/70"><MagnifyingGlass size={14} className="inline mr-1" />Buscar por nombre, RUT o email</Label>
                    <Input
                      placeholder="Buscar..."
                      value={workerSearch}
                      onChange={(e) => setWorkerSearch(e.target.value)}
                      className="bg-white border-[#2B4B8C]/10 text-[#2B4B8C]"
                      data-testid="search-workers"
                    />
                  </div>
                </div>
                <div className="max-h-64 overflow-y-auto rounded-md border border-[#2B4B8C]/10 bg-white">
                  {loadingWorkers ? (
                    <div className="text-center py-6 text-[#2B4B8C]/40 text-sm">Buscando...</div>
                  ) : availableWorkers.length === 0 ? (
                    <div className="text-center py-6 text-[#2B4B8C]/40 text-sm">No hay trabajadores disponibles</div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="border-[#2B4B8C]/10 hover:bg-transparent">
                          <TableHead className="text-[#2B4B8C]/60 text-xs">RUT</TableHead>
                          <TableHead className="text-[#2B4B8C]/60 text-xs">Nombre</TableHead>
                          <TableHead className="text-[#2B4B8C]/60 text-xs">Apellido</TableHead>
                          <TableHead className="text-[#2B4B8C]/60 text-xs">Cargo</TableHead>
                          <TableHead className="text-[#2B4B8C]/60 text-xs">Empresa</TableHead>
                          <TableHead className="text-[#2B4B8C]/60 text-xs">Estado</TableHead>
                          <TableHead className="text-[#2B4B8C]/60 text-xs text-right">Acción</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {availableWorkers.map((w) => {
                          const blocked = w.block_reasons && w.block_reasons.length > 0;
                          const canEnroll = w.eligible !== false;
                          return (
                            <TableRow key={w.id} className={`border-[#2B4B8C]/5 ${blocked ? 'bg-red-50/40' : 'hover:bg-[#2BBAA4]/5'}`}>
                              <TableCell className="text-[#2B4B8C]/60 text-sm py-2">{w.rut || '-'}</TableCell>
                              <TableCell className="text-[#2B4B8C] text-sm font-medium py-2">{w.first_name}</TableCell>
                              <TableCell className="text-[#2B4B8C] text-sm py-2">{w.last_name}</TableCell>
                              <TableCell className="text-[#2B4B8C]/60 text-sm py-2">{w.cargo || '-'}</TableCell>
                              <TableCell className="text-[#2B4B8C]/60 text-sm py-2">{w.company_name || '-'}</TableCell>
                              <TableCell className="py-2">
                                {blocked ? (
                                  <div className="flex items-start gap-1" title={w.block_reasons.join(', ')}>
                                    <Warning size={14} className="text-red-500 shrink-0 mt-0.5" />
                                    <div className="flex flex-col gap-0.5">
                                      {w.block_reasons.map((r, i) => (
                                        <span key={i} className="text-xs text-red-500 whitespace-normal break-words">{r.replace(/^R\d: /, '')}</span>
                                      ))}
                                    </div>
                                  </div>
                                ) : (
                                  <Badge className="bg-green-500/10 text-green-600 border-green-500/20 border text-[10px]">Habilitado</Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-right py-2">
                                {blocked && w.block_reasons?.some(r => r.includes('aprobado')) ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => { setViolationWorker(w); setViolationReason(''); }}
                                    className="border-rose-500/30 text-rose-600 hover:bg-rose-50 h-7 text-xs px-2"
                                    data-testid={`life-saving-${w.id}`}
                                  >
                                    <HeartBreak size={14} className="mr-1" /> Reinstruir
                                  </Button>
                                ) : canEnroll ? (
                                  <Button
                                    size="sm"
                                    disabled={enrollingWorker}
                                    onClick={() => handleEnrollWorker(w.id)}
                                    className="bg-[#2BBAA4] text-black hover:bg-[#239986] h-7 text-xs px-3"
                                    data-testid={`enroll-${w.id}`}
                                  >
                                    <Plus size={14} className="mr-1" /> Inscribir
                                  </Button>
                                ) : (
                                  <Badge className="bg-red-500/10 text-red-400 border-red-500/20 border text-[10px]">Bloqueado</Badge>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </div>
                <Button type="button" onClick={() => setShowWorkerSearch(false)} className="bg-[#2B4B8C] text-white hover:bg-[#2B4B8C]/80"><XCircle size={16} className="mr-1.5" /> Cerrar Búsqueda</Button>
              </CardContent>
            </Card>
          )}

          {/* Enrolled workers table */}
          <Card className="bg-white border-[#2B4B8C]/10">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#2B4B8C]/10 hover:bg-transparent">
                    <TableHead className="text-[#2B4B8C]/60">RUT</TableHead>
                    <TableHead className="text-[#2B4B8C]/60">Nombre</TableHead>
                    <TableHead className="text-[#2B4B8C]/60">Cargo</TableHead>
                    <TableHead className="text-[#2B4B8C]/60">Nº Vez</TableHead>
                    <TableHead className="text-[#2B4B8C]/60">Email</TableHead>
                    <TableHead className="text-[#2B4B8C]/60">Correo enviado a</TableHead>
                    <TableHead className="text-[#2B4B8C]/60">Asistencia</TableHead>
                    <TableHead className="text-[#2B4B8C]/60">Evaluación</TableHead>
                    <TableHead className="text-[#2B4B8C]/60 text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {confirmedEnrollments.map((e) => (
                    <TableRow key={e.id} className="border-[#2B4B8C]/5 hover:bg-[#f8fafc]">
                      <TableCell className="text-[#2B4B8C]/60">{e.worker_rut || '-'}</TableCell>
                      <TableCell className="text-[#2B4B8C] font-medium">{e.worker_name}</TableCell>
                      <TableCell className="text-[#2B4B8C]/60">{e.worker_cargo || '-'}</TableCell>
                      <TableCell><CourseCountBadge count={e.course_count} /></TableCell>
                      <TableCell className="text-[#2B4B8C]/60">{e.worker_email}</TableCell>
                      <TableCell data-testid={`sent-to-${e.id}`}>
                        {e.invitation_sent_to ? (
                          <div className="flex flex-col">
                            <span className="text-[#2B4B8C] text-xs font-medium flex items-center gap-1">
                              <CheckCircle size={12} className="text-green-500" /> {e.invitation_sent_to}
                            </span>
                            {e.invitation_sent_at && (
                              <span className="text-[#2B4B8C]/40 text-[11px]">
                                {new Date(e.invitation_sent_at).toLocaleString('es-CL', { timeZone: 'America/Santiago', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-[#2B4B8C]/30 text-xs">Sin enviar</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {e.attended ? (
                          <div className="flex items-center gap-1.5">
                            <Badge className="bg-green-500/10 text-green-500 border-green-500/20 border text-xs">
                              <CheckCircle size={12} className="mr-1" /> Asistió
                            </Badge>
                            <Button
                              variant="ghost" size="sm"
                              className="h-6 px-1.5 text-xs text-[#2B4B8C]/50 hover:text-[#2B4B8C]"
                              onClick={async () => { await enrollmentsApi.markAttendance(e.id, { attended: false }); toast.success('Asistencia quitada'); fetchEnrollments(); }}
                              data-testid={`unmark-attendance-${e.id}`}
                              title="Quitar asistencia"
                            >
                              <PencilSimple size={13} />
                            </Button>
                          </div>
                        ) : (
                          <Button
                            variant="outline" size="sm"
                            className="text-xs border-[#2BBAA4]/30 text-[#2BBAA4] h-7"
                            onClick={async () => { await enrollmentsApi.markAttendance(e.id, { attended: true }); toast.success('Asistencia marcada'); fetchEnrollments(); }}
                            data-testid={`mark-attendance-${e.id}`}
                          >
                            Marcar Asistencia
                          </Button>
                        )}
                      </TableCell>
                      <TableCell>
                        {(e.status === 'approved' || e.resultado === 'Aprobado') ? (
                          <div className="flex items-center gap-1.5">
                            <Badge className="bg-green-500/10 text-green-500 border-green-500/20 border text-xs">
                              Aprobado {e.score != null ? `(${e.score}%)` : ''}
                            </Badge>
                            <Button
                              variant="ghost" size="sm"
                              className="h-6 px-1.5 text-xs text-[#2B4B8C]/50 hover:text-[#2B4B8C]"
                              onClick={() => { setEvaluatingEnrollment(e); setEvalData({ result: 'approved', score: e.score != null ? e.score : 100, notes: e.evaluation_notes || '' }); }}
                              data-testid={`edit-eval-${e.id}`}
                              title="Editar evaluación"
                            >
                              <PencilSimple size={13} />
                            </Button>
                          </div>
                        ) : e.course_approved ? (
                          <Badge className="bg-green-500/10 text-green-500 border-green-500/20 border text-xs" data-testid={`course-approved-${e.id}`} title="Ya aprobó esta capacitación en otra fecha">
                            Aprobado {e.course_approved_score != null ? `(${e.course_approved_score}%)` : ''}
                          </Badge>
                        ) : (e.status === 'failed' || e.resultado === 'Reprobado') ? (
                          <div className="flex items-center gap-1.5">
                            <Badge className="bg-red-500/10 text-red-400 border-red-500/20 border text-xs">
                              Reprobado {e.score != null ? `(${e.score}%)` : ''}
                            </Badge>
                            <Button
                              variant="ghost" size="sm"
                              className="h-6 px-1.5 text-xs text-[#2B4B8C]/50 hover:text-[#2B4B8C]"
                              onClick={() => { setEvaluatingEnrollment(e); setEvalData({ result: 'failed', score: e.score != null ? e.score : 0, notes: e.evaluation_notes || '' }); }}
                              data-testid={`edit-eval-${e.id}`}
                              title="Editar evaluación"
                            >
                              <PencilSimple size={13} />
                            </Button>
                          </div>
                        ) : (
                          <Button
                            variant="outline" size="sm"
                            className="text-xs border-[#2B4B8C]/20 text-[#2B4B8C] h-7"
                            onClick={() => { setEvaluatingEnrollment(e); setEvalData({ result: 'approved', score: 100, notes: '' }); }}
                            data-testid={`evaluate-${e.id}`}
                          >
                            <Exam size={14} className="mr-1" /> Evaluar
                          </Button>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {training.status === 'conformada' && (
                          <Button variant="ghost" size="sm" onClick={() => { setCcEmail(''); setSingleSendEnrollment(e); }} className="text-[#2BBAA4] hover:text-[#239986]" title="Enviar invitación" data-testid={`send-invite-${e.id}`}>
                            <PaperPlaneTilt size={16} />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => setRemovingEnrollment(e)} className="text-red-400 hover:text-red-500" data-testid={`remove-enrollment-${e.id}`}>
                          <Trash size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {confirmedEnrollments.length === 0 && (
                <div className="text-center py-12 text-[#2B4B8C]/40">
                  No hay trabajadores inscritos
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Remove enrollment confirmation */}
      {removingEnrollment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="remove-confirm-overlay">
          <Card className="w-96 bg-white border-[#2B4B8C]/10 shadow-lg">
            <CardContent className="p-6 space-y-4">
              <p className="font-medium text-[#2B4B8C]">Eliminar Inscripción</p>
              <p className="text-sm text-[#2B4B8C]/70">
                ¿Eliminar la inscripción de <strong>{removingEnrollment.worker_name}</strong>?
              </p>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setRemovingEnrollment(null)} className="border-[#2B4B8C]/10" data-testid="cancel-remove">Cancelar</Button>
                <Button onClick={() => handleRemoveEnrollment(removingEnrollment.id)} className="bg-red-500 text-white hover:bg-red-600" data-testid="confirm-remove">Eliminar</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Conformar Modal */}
      {showConformarModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="conformar-overlay">
          <Card className="w-[420px] bg-white border-[#2B4B8C]/10 shadow-lg">
            <CardContent className="p-6 space-y-4">
              <p className="font-medium text-[#2B4B8C] text-lg">Conformar Capacitación</p>
              <p className="text-sm text-[#2B4B8C]/70">
                ¿Desea enviar un correo masivo a los <strong>{enrollments.length} participantes</strong> notificándoles las coordenadas de la capacitación?
              </p>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#2B4B8C]/60">Copia (CC) — opcional</Label>
                <Input
                  type="email"
                  value={conformarCc}
                  onChange={(ev) => setConformarCc(ev.target.value)}
                  placeholder="correo@ejemplo.cl"
                  className="border-[#2B4B8C]/15 text-sm"
                  data-testid="conformar-cc-input"
                />
                <p className="text-[11px] text-[#2B4B8C]/40">Se enviará una copia de cada invitación a este correo.</p>
              </div>
              <div className="flex flex-col gap-2 pt-2">
                <Button onClick={() => handleConformar(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="conformar-with-email">
                  Conformar y Enviar Correos
                </Button>
                <Button onClick={() => handleConformar(false)} variant="outline" className="border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="conformar-without-email">
                  Conformar sin Enviar Correos
                </Button>
                <Button onClick={() => setShowConformarModal(false)} variant="ghost" className="text-[#2B4B8C]/50" data-testid="cancel-conformar">
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Send massive invitations modal (with CC) */}
      {showSendModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowSendModal(false)} data-testid="send-massive-overlay">
          <Card className="w-[420px] bg-white border-[#2B4B8C]/10 shadow-lg" onClick={(ev) => ev.stopPropagation()}>
            <CardContent className="p-6 space-y-4">
              <p className="font-medium text-[#2B4B8C] text-lg flex items-center gap-2"><PaperPlaneTilt size={20} /> Enviar invitaciones</p>
              <p className="text-sm text-[#2B4B8C]/70">
                Se enviará el correo de invitación a los <strong>{confirmedEnrollments.length} inscrito(s)</strong>.
              </p>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#2B4B8C]/60">Copia (CC) — opcional</Label>
                <Input
                  type="email"
                  value={ccEmail}
                  onChange={(ev) => setCcEmail(ev.target.value)}
                  placeholder="correo@ejemplo.cl"
                  className="border-[#2B4B8C]/15 text-sm"
                  data-testid="send-massive-cc-input"
                />
                <p className="text-[11px] text-[#2B4B8C]/40">Se enviará una copia de cada correo a este destinatario.</p>
              </div>
              <div className="flex gap-2 justify-end pt-1">
                <Button variant="outline" onClick={() => setShowSendModal(false)} className="border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="cancel-send-massive">Cancelar</Button>
                <Button onClick={handleSendInvitations} disabled={sendingInvites} className="bg-[#2BBAA4] text-white hover:bg-[#239986]" data-testid="confirm-send-massive">
                  {sendingInvites ? 'Enviando...' : 'Enviar'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Send single invitation modal (with CC) */}
      {singleSendEnrollment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setSingleSendEnrollment(null)} data-testid="send-single-overlay">
          <Card className="w-[420px] bg-white border-[#2B4B8C]/10 shadow-lg" onClick={(ev) => ev.stopPropagation()}>
            <CardContent className="p-6 space-y-4">
              <p className="font-medium text-[#2B4B8C] text-lg flex items-center gap-2"><PaperPlaneTilt size={20} /> Enviar invitación</p>
              <p className="text-sm text-[#2B4B8C]/70">
                Destinatario: <strong>{singleSendEnrollment.worker_name}</strong> ({singleSendEnrollment.worker_email})
              </p>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#2B4B8C]/60">Copia (CC) — opcional</Label>
                <Input
                  type="email"
                  value={ccEmail}
                  onChange={(ev) => setCcEmail(ev.target.value)}
                  placeholder="correo@ejemplo.cl"
                  className="border-[#2B4B8C]/15 text-sm"
                  data-testid="send-single-cc-input"
                />
              </div>
              <div className="flex gap-2 justify-end pt-1">
                <Button variant="outline" onClick={() => setSingleSendEnrollment(null)} className="border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="cancel-send-single">Cancelar</Button>
                <Button onClick={() => handleSendSingle(singleSendEnrollment.worker_id, ccEmail.trim())} className="bg-[#2BBAA4] text-white hover:bg-[#239986]" data-testid="confirm-send-single">
                  Enviar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Direct Evaluation Dialog */}
      {evaluatingEnrollment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="evaluate-overlay">
          <Card className="w-[420px] bg-white border-[#2B4B8C]/10 shadow-lg">
            <CardContent className="p-6 space-y-4">
              <p className="font-medium text-[#2B4B8C]">Evaluar Trabajador</p>
              <p className="text-sm text-[#2B4B8C]/70">
                Evaluación directa de <strong>{evaluatingEnrollment.worker_name}</strong>
              </p>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs text-[#2B4B8C]/70">Resultado</Label>
                  <Select value={evalData.result} onValueChange={(v) => setEvalData({ ...evalData, result: v })}>
                    <SelectTrigger className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="eval-result">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#2B4B8C]/10">
                      <SelectItem value="approved">Aprobado</SelectItem>
                      <SelectItem value="failed">Reprobado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-[#2B4B8C]/70">Nota (%)</Label>
                  <Input type="number" min="0" max="100" value={evalData.score} onChange={(e) => setEvalData({ ...evalData, score: parseInt(e.target.value) || 0 })} className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="eval-score" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-[#2B4B8C]/70">Observaciones</Label>
                  <Input value={evalData.notes} onChange={(e) => setEvalData({ ...evalData, notes: e.target.value })} className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" placeholder="Opcional" data-testid="eval-notes" />
                </div>
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" onClick={() => setEvaluatingEnrollment(null)} className="border-[#2B4B8C]/10" data-testid="cancel-eval">Cancelar</Button>
                <Button onClick={handleDirectEvaluate} className={evalData.result === 'approved' ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-red-500 hover:bg-red-600 text-white'} data-testid="confirm-eval">
                  {evalData.result === 'approved' ? 'Aprobar' : 'Reprobar'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Life-Saving Violation Dialog */}
      <Dialog open={!!violationWorker} onOpenChange={() => setViolationWorker(null)}>
        <DialogContent className="bg-white border-[#2B4B8C]/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#2B4B8C] flex items-center gap-2">
              <HeartBreak size={20} className="text-rose-600" /> Registrar Transgresion
            </DialogTitle>
            <DialogDescription className="text-[#2B4B8C]/60 pt-1">
              Registrar transgresion de "Regla que salva vidas" para <strong>{violationWorker?.first_name} {violationWorker?.last_name}</strong> en el curso <strong>{training?.course_name}</strong>. Esto permitira reinscribir al trabajador a pesar de tener el curso vigente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <Label className="text-xs text-[#2B4B8C]/70 mb-1 block">Motivo de la transgresion</Label>
              <Textarea
                value={violationReason}
                onChange={(e) => setViolationReason(e.target.value)}
                placeholder="Describe brevemente la regla transgredida..."
                rows={3}
                className="border-[#2B4B8C]/15 resize-none"
                data-testid="violation-reason"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setViolationWorker(null)} className="border-[#2B4B8C]/10">Cancelar</Button>
              <Button onClick={handleLifeSavingViolation} className="bg-rose-600 text-white hover:bg-rose-700" data-testid="confirm-violation-btn">
                <HeartBreak size={14} className="mr-1.5" /> Registrar y Habilitar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Test URL / QR Modal */}
      {showTestUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowTestUrl(false)} data-testid="test-url-overlay">
          <Card className="w-[440px] max-h-[92vh] overflow-y-auto bg-white border-[#2B4B8C]/10 shadow-lg" onClick={(e) => e.stopPropagation()}>
            <CardContent className="p-6 space-y-4 text-center">
              <p className="font-medium text-[#2B4B8C] text-lg flex items-center justify-center gap-2"><QrCode size={20} /> QR y URL de la prueba</p>
              {(() => {
                const mode = training.test_mode || 'auto';
                const state = mode === 'auto' ? 'auto' : (training.test_active ? 'on' : 'off');
                const schedStr = training.scheduled_date ? new Date(training.scheduled_date).toLocaleDateString('es-CL', { timeZone: 'America/Santiago' }) : '-';
                const btn = (label, val, icon) => (
                  <button
                    type="button"
                    onClick={() => val === 'on' ? handleSetActivation('manual', true) : val === 'off' ? handleSetActivation('manual', false) : handleSetActivation('auto', false)}
                    className={`flex-1 flex items-center justify-center gap-1 py-1.5 text-sm rounded-md transition-colors ${state === val ? (val === 'off' ? 'bg-[#2B4B8C] text-white' : 'bg-[#2BBAA4] text-black') : 'text-[#2B4B8C]/60 hover:bg-white'}`}
                    data-testid={`test-state-${val}`}
                  >
                    {icon} {label}
                  </button>
                );
                return (
                  <>
                    <div className={`rounded-lg p-3 text-sm text-left ${state === 'on' ? 'bg-green-50 border border-green-200 text-green-700' : state === 'auto' ? 'bg-amber-50 border border-amber-200 text-amber-800' : 'bg-[#f8fafc] border border-[#2B4B8C]/10 text-[#2B4B8C]/70'}`} data-testid="test-state-label">
                      {state === 'on' && <><strong>Prueba ACTIVA</strong> · Modo manual. Los trabajadores ya pueden rendir.</>}
                      {state === 'off' && <><strong>Prueba INACTIVA</strong> · Modo manual. Actívala cuando corresponda.</>}
                      {state === 'auto' && (training.test_auto_start && training.test_auto_end
                        ? <><strong>Modo automático</strong> · La prueba se abrirá automáticamente el {schedStr} de {training.test_auto_start} a {training.test_auto_end} hrs (Chile).</>
                        : <><strong>Modo automático no configurado</strong> · Este curso no tiene horario de prueba. Configúralo en Configuración → Horarios de prueba, o usa On/Off.</>)}
                    </div>
                    <div className="flex gap-1 p-1 bg-[#f1f4f9] rounded-lg">
                      {btn('On', 'on', <Play size={14} />)}
                      {btn('Auto', 'auto', <ClockCounterClockwise size={14} />)}
                      {btn('Off', 'off', <XCircle size={14} />)}
                    </div>
                  </>
                );
              })()}
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/public/trainings/${trainingId}/qr.png?base=${encodeURIComponent(window.location.origin)}`}
                alt="QR prueba"
                className="w-44 h-44 mx-auto border border-[#2B4B8C]/10 rounded-lg"
                data-testid="test-qr-img"
              />
              <p className="text-xs text-[#2B4B8C]/50">Los trabajadores escanean este QR e ingresan su RUT para rendir la prueba.</p>
              <div className="bg-[#f8fafc] rounded-lg p-2 break-all text-xs text-[#2B4B8C]">
                {`${window.location.origin}/prueba/${trainingId}`}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Button variant="outline" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/prueba/${trainingId}`); toast.success('URL copiada'); }} className="border-[#2B4B8C]/15 text-[#2B4B8C] text-xs" data-testid="copy-test-url">Copiar link</Button>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/public/trainings/${trainingId}/qr.png?base=${encodeURIComponent(window.location.origin)}`} download={`qr-prueba-${trainingId}.png`} className="inline-flex items-center justify-center rounded-md border border-[#2B4B8C]/15 text-[#2B4B8C] text-xs h-9 hover:bg-[#f8fafc]" data-testid="download-qr">Descargar QR</a>
                <a href={`${window.location.origin}/prueba/${trainingId}`} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center rounded-md border border-[#2B4B8C]/15 text-[#2B4B8C] text-xs h-9 hover:bg-[#f8fafc]" data-testid="open-test-url">Abrir</a>
              </div>
              <Button onClick={() => setShowTestUrl(false)} className="w-full bg-[#2B4B8C] text-white hover:bg-[#233d75]">Cerrar</Button>
            </CardContent>
          </Card>
        </div>
      )}
      {/* Email Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowPreview(false)} data-testid="preview-overlay">
          <Card className="w-full max-w-3xl max-h-[90vh] bg-white border-[#2B4B8C]/10 shadow-lg flex flex-col" onClick={(e) => e.stopPropagation()}>
            <CardContent className="p-5 flex flex-col gap-3 overflow-hidden">
              <div className="flex items-center justify-between gap-3">
                <p className="font-medium text-[#2B4B8C] text-lg flex items-center gap-2"><Envelope size={20} /> Vista previa del correo</p>
                <Button variant="ghost" size="sm" onClick={() => setShowPreview(false)} className="text-[#2B4B8C]/60"><XCircle size={20} /></Button>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-[#2B4B8C]/60">Trabajador:</span>
                <select
                  value={previewWorkerId}
                  onChange={(e) => { setPreviewWorkerId(e.target.value); openPreview(e.target.value); }}
                  className="text-sm border border-[#2B4B8C]/15 rounded-md px-2 py-1 text-[#2B4B8C] bg-white"
                  data-testid="preview-worker-select"
                >
                  <option value="">(Primer inscrito)</option>
                  {confirmedEnrollments.map(e => (
                    <option key={e.worker_id} value={e.worker_id}>{e.worker_name} · {e.worker_rut}</option>
                  ))}
                </select>
                {previewData?.recipient_email && (
                  <span className="text-xs text-[#2B4B8C]/50">Para: <strong>{previewData.recipient_email}</strong></span>
                )}
              </div>
              <div className="border border-[#2B4B8C]/10 rounded-lg overflow-auto bg-[#f4f6fb]" style={{ height: '60vh' }}>
                {loadingPreview ? (
                  <div className="flex items-center justify-center h-full"><div className="animate-spin rounded-full h-7 w-7 border-b-2 border-[#2BBAA4]" /></div>
                ) : (
                  <iframe title="preview" srcDoc={previewData?.html || ''} className="w-full h-full border-0" data-testid="preview-iframe" />
                )}
              </div>
              <p className="text-xs text-[#2B4B8C]/50">Cada trabajador recibe un correo individual con su propia bitácora del día.</p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
